class SmokeCalculator extends HTMLElement {
  #range = null;
  #dailyValue = null;
  #cigarettesMonthly = null;
  #kitMonthly = null;
  #kitHint = null;
  #savingsMonthly = null;
  #savingsYearlyAmount = null;
  #savingsYearlySuffix = null;
  #presetButtons = [];

  connectedCallback() {
    this.#range = this.querySelector('[data-ref="range"]');
    this.#dailyValue = this.querySelector('[data-ref="daily-value"]');
    this.#cigarettesMonthly = this.querySelector('[data-ref="cigarettes-monthly"]');
    this.#kitMonthly = this.querySelector('[data-ref="kit-monthly"]');
    this.#kitHint = this.querySelector('[data-ref="kit-hint"]');
    this.#savingsMonthly = this.querySelector('[data-ref="savings-monthly"]');
    this.#savingsYearlyAmount = this.querySelector('[data-ref="savings-yearly-amount"]');
    this.#savingsYearlySuffix = this.querySelector('[data-ref="savings-yearly-suffix"]');
    this.#presetButtons = Array.from(this.querySelectorAll('[data-ref="preset-button"]'));

    if (!this.#range) return;

    this.#range.addEventListener('input', () => this.#update());

    for (const button of this.#presetButtons) {
      button.addEventListener('click', () => {
        const nextValue = parseInt(button.dataset.value, 10) || 1;
        this.#range.value = String(nextValue);
        this.#update();
      });
    }

    this.#update();
  }

  #update() {
    const value = parseInt(this.#range?.value, 10) || 1;
    const min = parseInt(this.#range?.min, 10) || 1;
    const max = parseInt(this.#range?.max, 10) || 40;
    const progress = ((value - min) / (max - min)) * 100;
    this.style.setProperty('--range-progress', `${progress}%`);

    for (const button of this.#presetButtons) {
      button.classList.toggle('is-active', parseInt(button.dataset.value, 10) === value);
    }

    const packSize = this.#toNumber(this.dataset.packSize, 20);
    const packPrice = this.#toNumber(this.dataset.packPrice, 12.93);
    const savingsBaseMonthly = this.#toNumber(this.dataset.savingsBaseMonthly, 30);
    const kitPriceDisplay = this.dataset.kitPriceDisplay || '29,97€';
    const kitHintDisplay = this.dataset.kitHintDisplay || '~30€/mois';

    const cigarettesMonthlyTotal = Math.ceil((value / packSize) * packPrice * 30);
    const monthlySavings = Math.max(0, Math.ceil(cigarettesMonthlyTotal - savingsBaseMonthly));
    const yearlySavings = monthlySavings * 12;

    if (this.#dailyValue) {
      this.#dailyValue.textContent = String(value);
    }

    if (this.#cigarettesMonthly) {
      this.#cigarettesMonthly.textContent = this.#formatMoney(cigarettesMonthlyTotal, true, false);
    }

    if (this.#kitMonthly) {
      this.#kitMonthly.textContent = kitPriceDisplay;
    }

    if (this.#kitHint) {
      this.#kitHint.textContent = kitHintDisplay;
    }

    if (this.#savingsMonthly) {
      this.#savingsMonthly.textContent = `${this.#formatMoney(monthlySavings, true, false)}${this.#getMonthSuffix()}`;
    }

    if (this.#savingsYearlyAmount) {
      this.#savingsYearlyAmount.textContent = this.#formatMoney(yearlySavings, false, false);
    }

    if (this.#savingsYearlySuffix) {
      this.#savingsYearlySuffix.textContent = this.#getYearlySuffix();
    }
  }

  #toNumber(value, fallback = 0) {
    if (value == null || value === '') return fallback;
    const parsed = Number.parseFloat(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  #formatMoney(value, withApproxSymbol, withDecimals = true) {
    const locale = document.documentElement.lang || 'fr-FR';
    const symbol = '€';
    const approxSymbol = withApproxSymbol ? '~' : '';
    const formattedValue = new Intl.NumberFormat(locale, {
      minimumFractionDigits: withDecimals ? 2 : 0,
      maximumFractionDigits: withDecimals ? 2 : 0,
    }).format(Math.max(0, value));

    return `${approxSymbol}${formattedValue}${symbol}`;
  }

  #getMonthSuffix() {
    return this.dataset.monthSuffix || '/mois';
  }

  #getYearlySuffix() {
    return this.dataset.yearlySuffix || ' par an';
  }
}

if (!customElements.get('smoke-calculator')) {
  customElements.define('smoke-calculator', SmokeCalculator);
}
